import React, {
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { Box, Typography } from "@mui/material";
import {
  getViewerLabelPaddingSx,
  viewerPanelBodySx,
  viewerPanelSurfaceSx,
} from "./unityWebViewer/panelStyles";

const Label = ({
  labelPosition,
  displayName,
  subtitle,
  alignment = "auto", // "auto" flips left/right based on x
  offsetLeft = 0,
  sideGap = "5%",
  portal = false,
  portalZIndex = 30,
  labelRef,
  surfaceSx = viewerPanelSurfaceSx,
  guideMarkerLabel = "",
  guideMarkerId,
}) => {
  const hasSubtitle = Boolean(subtitle);
  const hasGuideMarker = Boolean(guideMarkerLabel);

  // Decide left vs right if in "auto" mode
  const effectiveAlignment = useMemo(() => {
    if (alignment === "auto") {
      const w = typeof window !== "undefined" ? window.innerWidth : 0;
      return labelPosition.x < w / 3 ? "right" : "left";
    }
    return alignment;
  }, [alignment, labelPosition.x]);

  // Map alignment to CSS translate
  const transform = useMemo(() => {
    switch (effectiveAlignment) {
      case "left":
        return `translate(calc(-100% - ${sideGap}), -50%)`;
      case "right":
        return `translate(${sideGap}, -50%)`;
      case "top":
        return "translate(-50%, -105%)";
      case "bottom":
        return "translate(-50%, 5%)";
      default:
        return "translate(-50%, -50%)";
    }
  }, [effectiveAlignment, sideGap]);

  const label = (
    <Box
      ref={labelRef}
      sx={{
        position: portal ? "fixed" : "absolute",
        top: labelPosition.y,
        left: labelPosition.x + offsetLeft,
        transform,
        zIndex: portal ? portalZIndex : undefined,
        ...surfaceSx,
        ...getViewerLabelPaddingSx(hasSubtitle || hasGuideMarker),
        color: "var(--label-text, #f2fffc)",
        boxSizing: "border-box",
        pointerEvents: "none",
        overflow: "hidden",
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: "var(--viewer-label-line-height, 1.15)",
        textAlign: "center",
        whiteSpace: "nowrap",
      }}
    >
      <Typography
        variant="h5"
        component="div"
        className="viewer-text-trim"
        sx={{
          color: "inherit",
          fontWeight: 700,
          fontSize: "var(--viewer-label-font-size, 19px)",
          fontSynthesis: "none",
          lineHeight: "var(--viewer-label-line-height, 1.15)",
          m: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {displayName}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          component="div"
          className="viewer-text-trim"
          sx={{
            color: "var(--label-subtitle, rgba(8, 48, 51, 0.8))",
            letterSpacing: "0.05em",
            fontSize: "var(--viewer-label-subtitle-font-size, 13px)",
            fontSynthesis: "none",
            lineHeight: "var(--viewer-label-subtitle-line-height, 1.15)",
            m: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontStyle: "italic",
          }}
        >
          {subtitle}
        </Typography>
      )}
      {hasGuideMarker && (
        <Box
          id={guideMarkerId}
          className="environment-guide-marker-row"
          sx={{
            ...viewerPanelBodySx,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "9px",
            color: "rgba(242, 255, 252, 0.82)",
            whiteSpace: "nowrap",
          }}
        >
          <span className="environment-guide-marker-dot" aria-hidden="true" />
          <span dir="auto">{guideMarkerLabel}</span>
        </Box>
      )}
    </Box>
  );

  if (portal && typeof document !== "undefined") {
    return createPortal(label, document.body);
  }

  return label;
};

export default Label;
